<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Paginator from 'primevue/paginator'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useFiltrosEnUrl } from '@/composables/useFiltrosEnUrl'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel, statusSeverity } from '@/utils/status'
import {
  followUpsApi,
  FOLLOW_UP_BUCKETS,
  FOLLOW_UP_BUCKET_LABELS,
  type FollowUpBucket,
  type FollowUpCounts,
  type FollowUpItem,
} from '@/services/followups'

/**
 * Bandeja de seguimientos.
 *
 * Es la respuesta a "qué tengo que hacer hoy". El backend agrupa las cotizaciones
 * en `vencido`, `hoy` y `proximo` y el cubo se pinta tal cual: recalcularlo en el
 * cliente con la fecha sería la forma más fácil de tener un desfase de un día por
 * zona horaria, y un seguimiento que sale como vencido cuando no toca hace que el
 * comercial lo ignore.
 *
 * `loading`, `error` e `items` van por separado: con un solo booleano, un fallo de
 * red se pintaría como "no hay seguimientos", que es un diagnóstico falso.
 */

const router = useRouter()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('followup.manage'))

/**
 * Los contadores a cero.
 *
 * Se usan para inicializar y también al fusionar lo que llega del servidor. El
 * backend los manda siempre en `meta`, pero la vista no puede dar por hecho que
 * un campo está: si faltara, `counts.overdue` en la plantilla reventaría el
 * render entero y la bandeja se quedaría en blanco, sin lista ni mensaje. Con la
 * fusión, un campo que falta sale a cero y lo demás sigue en pie.
 */
const CONTADORES_VACIOS: FollowUpCounts = { overdue: 0, today: 0, upcoming: 0, unread: 0 }

const items = ref<FollowUpItem[]>([])
const counts = ref<FollowUpCounts>({ ...CONTADORES_VACIOS })
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const search = ref('')
const bucket = ref<FollowUpBucket | ''>('')
const page = ref(1)
const total = ref(0)
const perPage = 20

/*
| Los filtros viven en la URL
|--------------------------------------------------------------------------
|
| Filtrabas el seguimiento, abrías una cotización, volvías, y el filtro había desaparecido.
| No era el botón de atrás: es que los filtros estaban en `ref`s de este componente, y un
| componente no sobrevive a que te vayas a otra vista.
|
| El cubo se valida contra `FOLLOW_UP_BUCKETS` porque llega de la URL y `?cubo=inventado`
| saldría en la consulta al backend, que no lo entiende.
*/
const filtros = useFiltrosEnUrl({
  busqueda: { ref: search, texto: true },
  cubo: { ref: bucket, permitidos: valoresDeCubo() },
  pagina: { ref: page, numero: true },
})

/** Los cubos que existen, para validar lo que llega por la URL. */
function valoresDeCubo(): FollowUpBucket[] {
  return FOLLOW_UP_BUCKETS.map((opcion) => opcion.value)
}

/**
 * Filas de la tabla de escritorio.
 *
 * Las etiquetas se calculan aquí y no con un `template` por columna: `DenseTable`
 * imprime `row[field]`, así que el formateo se resuelve una vez por fila. Los
 * `null` se convierten en "—" porque una celda vacía no se distingue de un dato
 * vacío de verdad.
 *
 * `id` duplica `quote_id` porque `DenseTable` usa `data-key="id"`, y sin él
 * PrimeVue no puede identificar las filas al paginar o al pintar.
 */
const rows = computed(() =>
  items.value.map((item) => ({
    ...item,
    id: item.quote_id,
    customer_label: item.customer_name,
    document_label: item.document ?? '—',
    status_label: statusLabel(item.status),
    total_label: formatCurrency(item.total_price),
    follow_up_label: formatShortDate(item.next_follow_up_date),
    bucket_label: FOLLOW_UP_BUCKET_LABELS[item.bucket],
    bucket_value: item.bucket,
    note_label: item.last_follow_up_note ?? '—',
    unread_label: item.unread_notes_count > 0 ? `${item.unread_notes_count} nueva(s)` : '',
  })),
)

/**
 * Columnas de la tabla densa.
 *
 * El cubo y la fecha van juntos en una sola columna porque son la misma
 * información: separado, obligarían a mirar dos celdas para responder "¿me toca
 * hoy?". El documento se pliega en móvil porque no se usa para decidir nada.
 */
/**
 * Cliente, con el aviso de notas sin leer.
 *
 * Una nota nueva sin señalar es una nota que nadie va a leer, y es justo la
 * información que la bandeja existe para highlightar. Se pinta junto al nombre
 * y no en una columna aparte para que no se pueda pasar por alto al recorrer la
 * lista de un vistazo.
 */
function customerCell(row: unknown): unknown {
  const item = row as FollowUpItem

  return h('span', { class: 'flex flex-col gap-0.5' }, [
    h('span', { class: 'flex items-center gap-2' }, [
      h('span', { class: 'font-medium' }, item.customer_name),
      item.unread_notes_count > 0
        ? h(
            'span',
            {
              class:
                'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
            },
            `${item.unread_notes_count} nueva(s)`,
          )
        : null,
    ]),
    item.document === null
      ? null
      : h('span', { class: 'text-xs font-normal text-surface-500' }, item.document),
  ])
}

/**
 * Celda del cubo: la etiqueta y la fecha.
 *
 * El cubo se pinta como el servidor lo calculó. Recalcularlo aquí a partir de la
 * fecha devolvería el mismo valor casi siempre y uno equivocado el resto, que es
 * justo cuando dejaría de servir.
 */
function bucketCell(row: unknown): unknown {
  const item = row as FollowUpItem

  return h('span', { class: 'flex items-center gap-2' }, [
    h(Tag, {
      value: FOLLOW_UP_BUCKET_LABELS[item.bucket],
      severity: bucketSeverity(item.bucket),
    }),
    h('span', { class: 'text-xs text-surface-500' }, formatShortDate(item.next_follow_up_date)),
  ])
}

/**
 * La celda del estado, con su color.
 *
 * El mismo módulo compartido que usan las cotizaciones, para que el color de un
 * estado sea el mismo en las dos pantallas y no dependa de qué copia se leyó al
 * escribir. Las etiquetas estaban aquí por tercera vez, después de la ficha y el
 * listado de cotizaciones.
 */
function statusCell(row: unknown): unknown {
  const item = row as FollowUpItem

  return h(Tag, {
    value: statusLabel(item.status),
    severity: statusSeverity(item.status),
  })
}

const visibleColumns = [
  { field: 'customer_label', header: 'Cliente', template: customerCell },
  { field: 'bucket_label', header: 'Seguimiento', template: bucketCell },
  { field: 'status_label', header: 'Estado', template: statusCell, hideOnMobile: true },
  { field: 'total_label', header: 'Importe' },
  { field: 'note_label', header: 'Última nota', hideOnMobile: true },
]

const isEmpty = computed(() => !loading.value && errorMessage.value === null && items.value.length === 0)
const hasFilters = computed(() => search.value.trim() !== '' || bucket.value !== '')

function bucketSeverity(value: FollowUpBucket): 'danger' | 'warn' | 'info' | 'secondary' {
  switch (value) {
    case 'vencido':
      return 'danger'
    case 'hoy':
      return 'warn'
    case 'proximo':
      return 'info'
    default:
      return 'secondary'
  }
}

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar la bandeja de seguimientos. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await followUpsApi.list({
      search: search.value.trim() || undefined,
      bucket: bucket.value === '' ? undefined : bucket.value,
      page: page.value,
      per_page: perPage,
    })

    items.value = data.data
    total.value = data.meta.total
    // Los contadores vienen sin el filtro de búsqueda a propósito: buscar
    // "Andina" no debe hacer desaparecer los recuentos de "hoy" y "vencidos",
    // o el usuario vería una lista vacía sin entender por qué.
    counts.value = { ...CONTADORES_VACIOS, ...data.meta.counts }
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  // Sin debounce, cada tecla dispara una petición y las respuestas pueden llegar
  // desordenadas, pintando resultados de un término anterior.
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch(bucket, () => {
  page.value = 1
  void load()
})

watch(page, () => {
  void load()
})

onMounted(() => {
  // Sin permiso no se ni se pide: la autorización real la aplica el backend,
  // pero un 403 de salida es un 403 frustrante que se puede evitar.
  if (!canManage.value) return

  void load()
})

onBeforeUnmount(() => {
  // Sin esto, el temporizador del debounce sobrevive a la vista y dispara una
  // petición que ya no le sirve a nadie. En los tests se nota porque la petición
  // se cuela en el test siguiente; en producción es una llamada wasting que
  // vuelve a pintar un componente que ya no existe.
  if (debounceHandle !== null) {
    clearTimeout(debounceHandle)
    debounceHandle = null
  }
})

function selectBucket(value: FollowUpBucket | ''): void {
  bucket.value = value
}

function clearFilters(): void {
  filtros.limpiar()
  page.value = 1
  void load()
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

/** `DenseTable` tipa las filas como `unknown`; aquí se estrechan. */
function openQuoteFromRow(row: unknown): void {
  openQuote(row as FollowUpItem)
}

function openQuote(item: FollowUpItem): void {
  void router.push(filtros.conQuery({ name: 'quotes.show', params: { id: String(item.quote_id) } }))
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header>
      <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
        Seguimientos
      </h1>
      <p class="mt-1 text-surface-500 dark:text-surface-400">
        Cotizaciones con próximo contacto previsto
      </p>
    </header>

    <Message
      v-if="!canManage"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      Tu rol no tiene el permiso <code>followup.manage</code>, así que no puede ver
      la bandeja de seguimientos.
    </Message>

    <template v-else>
      <!-- Contadores: el resumen de lo que hay que hacer, no un adorno. -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <button
          v-for="item in [
            { value: 'vencido' as const, label: 'Vencidos', count: counts.overdue, icon: 'pi pi-exclamation-circle', tone: 'text-red-600 dark:text-red-400' },
            { value: 'hoy' as const, label: 'Para hoy', count: counts.today, icon: 'pi pi-calendar', tone: 'text-amber-600 dark:text-amber-400' },
            { value: 'proximo' as const, label: 'Próximos', count: counts.upcoming, icon: 'pi pi-calendar-clock', tone: 'text-sky-600 dark:text-sky-400' },
            { value: '' as const, label: 'Con notas nuevas', count: counts.unread, icon: 'pi pi-envelope', tone: 'text-surface-700 dark:text-surface-200' },
          ]"
          :key="item.label"
          type="button"
          :aria-pressed="bucket === item.value"
          :class="[
            'rounded-lg border p-4 text-left transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:focus-visible:ring-offset-surface-900',
            bucket === item.value
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-surface-200 bg-white hover:bg-surface-50 dark:border-surface-700 dark:bg-surface-900 dark:hover:bg-surface-800',
          ]"
          @click="selectBucket(item.value)"
        >
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm text-surface-500 dark:text-surface-400">
              {{ item.label }}
            </p>
            <i
              :class="[item.icon, item.tone]"
              aria-hidden="true"
            />
          </div>
          <p
            :class="['mt-1 text-2xl font-semibold', item.tone]"
            :data-testid="`follow-up-count-${item.value || 'unread'}`"
          >
            {{ item.count }}
          </p>
        </button>
      </div>

      <AppCard>
        <template #header>
          <form
            class="flex flex-col gap-3 sm:flex-row"
            role="search"
            @submit.prevent
          >
            <InputText
              v-model="search"
              placeholder="Buscar por cliente o documento"
              aria-label="Buscar seguimientos"
              class="w-full"
            />
            <Button
              v-if="hasFilters"
              label="Limpiar"
              icon="pi pi-filter-slash"
              severity="secondary"
              text
              @click="clearFilters"
            />
          </form>
        </template>

        <div
          v-if="errorMessage"
          class="flex flex-col items-center gap-3 py-12 text-center"
          role="alert"
        >
          <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
          <p class="font-medium text-surface-900 dark:text-surface-100">
            No se pudo cargar la bandeja
          </p>
          <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
            {{ errorMessage }}
          </p>
          <Button
            label="Reintentar"
            icon="pi pi-refresh"
            severity="secondary"
            @click="load"
          />
        </div>

        <div
          v-else-if="loading"
          class="py-12 text-center text-surface-500"
          aria-busy="true"
        >
          <i class="pi pi-spin pi-spinner text-2xl" />
          <p class="mt-2 text-sm">
            Cargando seguimientos…
          </p>
        </div>

        <div
          v-else-if="isEmpty"
          class="py-12 text-center"
        >
          <i class="pi pi-calendar-times text-5xl text-surface-300 dark:text-surface-600" />
          <h2 class="mt-3 text-lg font-medium text-surface-900 dark:text-surface-100">
            {{ hasFilters ? 'Ningún seguimiento coincide' : 'No hay seguimientos' }}
          </h2>
          <p class="mt-1 text-surface-500 dark:text-surface-400">
            <template v-if="hasFilters">
              Prueba a cambiar la búsqueda o a quitar el filtro de cubo.
            </template>
            <template v-else>
              Cuando a una cotización se le ponga una fecha de seguimiento,
              aparecerá aquí.
            </template>
          </p>
          <Button
            v-if="hasFilters"
            label="Limpiar filtros"
            icon="pi pi-filter-slash"
            severity="secondary"
            class="mt-4"
            @click="clearFilters"
          />
        </div>

        <template v-else>
          <!-- Escritorio: `DenseTable`, el mismo componente que usan catálogo y
               clientes. Una tabla escrita a mano tenía su propio padding y su
               propio cuerpo de letra, así que las filas de esta sección medían
               distinto de las de las demás. -->
          <DenseTable
            v-if="isDesktop"
            :columns="visibleColumns"
            :rows="rows"
            :loading="loading"
            :paginator="false"
            @row-click="openQuoteFromRow"
            @action="openQuoteFromRow"
          />

          <!-- Móvil: lista de cards, porque una tabla de cinco columnas en un
               teléfono obliga a hacer scroll horizontal para leer el cliente. -->
          <ul
            v-else
            class="space-y-3"
            data-testid="follow-ups-cards"
          >
            <li
              v-for="item in items"
              :key="item.quote_id"
            >
              <button
                type="button"
                class="w-full rounded-lg border border-surface-200 bg-white p-3 text-left transition-colors hover:bg-surface-50 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-surface-700 dark:bg-surface-900 dark:hover:bg-surface-800 dark:focus-visible:ring-offset-surface-900"
                @click="openQuote(item)"
              >
                <div class="flex items-start justify-between gap-2">
                  <span class="min-w-0 truncate font-medium text-surface-900 dark:text-surface-100">
                    {{ item.customer_name }}
                  </span>
                  <Tag
                    :value="FOLLOW_UP_BUCKET_LABELS[item.bucket]"
                    :severity="bucketSeverity(item.bucket)"
                  />
                </div>
                <p class="mt-1 text-xs text-surface-500 dark:text-surface-400">
                  {{ item.next_follow_up_date ? formatShortDate(item.next_follow_up_date) : 'Sin fecha' }}
                  · {{ item.status }}
                </p>
                <p
                  v-if="item.last_follow_up_note"
                  class="mt-1 truncate text-sm text-surface-600 dark:text-surface-300"
                >
                  {{ item.last_follow_up_note }}
                </p>
                <div class="mt-2 flex items-center justify-between">
                  <span class="text-sm font-medium text-surface-900 dark:text-surface-100">
                    {{ formatCurrency(item.total_price) }}
                  </span>
                  <span
                    v-if="item.unread_notes_count > 0"
                    class="text-xs font-medium text-amber-700 dark:text-amber-300"
                  >
                    {{ item.unread_notes_count }} nota(s) nueva(s)
                  </span>
                </div>
              </button>
            </li>
          </ul>

          <Paginator
            v-if="total > perPage"
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            :rows-per-page-options="[20]"
            class="mt-4"
            @page="onPageChange"
          />
        </template>
      </AppCard>
    </template>
  </div>
</template>
